import { ApiError, type InstanceSeatsView, type InstanceSharePolicy } from "@keydials/api-client";
import { licensingSummary } from "./licensing";

/**
 * What the People sheet says about seats, and what it says when a grant is refused (KEY-487).
 *
 * A pure module for the same reason `memberRows.ts` is: these two decisions are the ones worth testing,
 * and web-user has no DOM harness. Before this, seat enforcement was invisible from the sheet — the
 * server refused with the plan's own numbers and the sheet replaced them with "you may not have permission".
 */

export interface SeatLine {
  /** The sentence to show above the role selector. */
  text: string;
  /** Every seat is taken. Inviting a NEW person is certain to be refused, so the button says so instead. */
  full: boolean;
}

/**
 * The seat position as one sentence, or null when there is nothing worth saying: a host without billing,
 * or a plan that does not cap users, both arrive with no ceiling to count against.
 *
 * <b>Delegates to `licensing.ts` (KEY-497).</b> The Licensing section reads exactly the same figure at
 * length, and two hand-written phrasings of one number is how the sheet and the panel end up disagreeing
 * about the same plan — so the wording lives in one module and this one narrows it to the sheet's needs.
 * That is also what moved the wording from "seats" to "places", which is the word the server's own refusal
 * uses.
 */
export function seatLine(seats: InstanceSeatsView | null | undefined): SeatLine | null {
  // Only the counted state has a sentence worth putting above a role selector: "nobody is counting" and
  // "unlimited" both mean the next grant cannot be refused, and a line saying so is noise here.
  const summary = licensingSummary(seats);
  return summary.state === "counted" ? { text: summary.headline, full: summary.full } : null;
}

/**
 * Whether the primary button should refuse to try. Only an invite of somebody new is certain to need a
 * seat; an existing organisation member may already hold one through another dial (a membership anywhere
 * is the rule), so that path stays open and the server decides.
 */
export function inviteBlockedBySeats(seats: InstanceSeatsView | null | undefined, inviting: boolean): boolean {
  return inviting && seatLine(seats)?.full === true;
}

/**
 * The client's own fallback shapes when the server sent no usable message: `apiFailure`'s "(HTTP n)", and the
 * api-client's bare "POST /api/… failed: 404" status line, which reached a user verbatim (KEY-931).
 */
const NO_DETAIL = /\(HTTP \d+\)$|^[A-Z]+ \/\S* failed: \d+$/;

/**
 * The sentence shown when a grant fails. The server's explanation is preferred wherever there is one —
 * a seat refusal names the tier and the numbers, an invite refusal names the door to use instead — and
 * the fixed wording is kept only for the two cases where the server says nothing useful.
 */
export function grantFailureMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return "Only an organisation administrator can make someone a dial admin.";
    if (error.message && !NO_DETAIL.test(error.message)) return error.message;
    if (error.status === 404) return "You can't give people access to this dial. Ask an administrator of its organisation.";
  }
  return "Couldn't grant that access. You may not have permission for that role.";
}

/**
 * The sentence shown when a guest invite fails. A 409 keeps its own wording because it names the door to
 * use instead; anything else the server explained — a closed guest channel (KEY-546) above all — is shown
 * as the server said it, rather than blaming the email address for a refusal that has nothing to do with it.
 */
export function guestInviteFailureMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return "That email already has an account here — add them under People instead.";
    if (error.message && !NO_DETAIL.test(error.message)) return error.message;
  }
  return "Couldn't create that invite. Check the email address and try again.";
}

export type ShareChannelName = keyof InstanceSharePolicy;

/**
 * Per-channel wording for the two policy refusals (KEY-556). The server's own `Closed` sentence ("doesn't
 * allow this dial to be shared this way") is right for a failed write but too vague for a page that has
 * replaced its form with it: here the person needs to know which door is shut and what still works.
 */
const REFUSALS: Record<ShareChannelName, { Closed: string; OrganisationManagerOnly: string }> = {
  member: {
    Closed:
      "Your organisation has closed adding people to its dials, so nobody can be given access here. " +
      "People already on this dial keep their access, and you can still remove it.",
    OrganisationManagerOnly:
      "Your organisation only lets an organisation administrator give people access to its dials. " +
      "Ask one of them to add who you need. You can still remove access from people already here.",
  },
  guest: {
    Closed:
      "Your organisation has closed guest invites, so nobody can invite a guest to this dial. " +
      "You can still see and revoke the guests already here.",
    OrganisationManagerOnly:
      "Your organisation only lets an organisation administrator invite guests. " +
      "Ask one of them to send the invite. You can still see and revoke the guests already here.",
  },
  grant: {
    Closed: "Your organisation doesn't allow read access to its dials to be granted.",
    OrganisationManagerOnly:
      "Your organisation only lets an organisation administrator grant read access to its dials. Ask one of them to do it.",
  },
  feed: {
    Closed:
      "Your organisation has closed feeding its dials to other organisations, so this dial can't be fed to anyone. " +
      "You can still stop a feed, and other dials can still feed this one.",
    OrganisationManagerOnly:
      "Your organisation only lets an organisation administrator feed its dials to other organisations. " +
      "Ask one of them to set it up. You can still stop a feed, and other dials can still feed this one.",
  },
};

/**
 * The wording when the dial itself is the reason (KEY-560). A test dial stays with the people it was sent to,
 * whatever the organisation allows, so none of these blame the organisation. People is shut only on the test dial of
 * a dial somebody built themselves, which they try alone. On any other test dial its own admins can still add
 * colleagues.
 */
const TEST_DIAL_REFUSALS: Partial<Record<ShareChannelName, string>> = {
  member: "This is a test dial for a dial you built, so you try it alone. Nobody else can be added to it.",
  guest: "This is a test dial, so it stays with the people it was sent to. Guests can't be invited to it.",
  grant: "This is a test dial, so it stays with the people it was sent to. Read access to it can't be granted.",
  feed:
    "This is a test dial, so it stays with the people it was sent to. It can't be fed to another organisation, " +
    "and other organisations can't feed it.",
};

/**
 * Why this channel is shut on this dial, in words — or null when it isn't.
 *
 * ⚠️ <b>This is the policy's answer, never the caller's role.</b> `allowed` says nothing about whether the
 * caller could share at all, so callers must only show this to somebody who otherwise could (a dial admin);
 * shown to a Reader it would blame the organisation for something they were never able to do.
 *
 * Null for a missing policy too: an older payload predates restrictions, so nothing is restricted.
 */
export function shareRefusal(policy: InstanceSharePolicy | null | undefined, channel: ShareChannelName): string | null {
  const answer = policy?.[channel];
  if (!answer || answer.allowed) return null;
  const reason = answer.blockReason;
  if (reason === "Closed" || reason === "OrganisationManagerOnly") return REFUSALS[channel][reason];
  if (reason === "CheckingTestDial") {
    return TEST_DIAL_REFUSALS[channel] ?? (answer.message || "This is a test dial, so it stays with the people it was sent to.");
  }
  return answer.message || "Your organisation doesn't allow this dial to be shared this way.";
}
